/**
 * Boli Live - Product Card Component
 *
 * eBay-style fixed-price product listing card with wishlist support.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Star, ShoppingCart, Check, Loader2 } from 'lucide-react';
import type { Product } from '@boli/types';
import { cn } from '../lib/utils';
import { formatPKR } from '../lib/utils';
import { useCartStore } from '../lib/stores/cart-store';

interface ProductCardProps {
  product: Product & {
    sellerName?: string;
    sellerRating?: number;
    storeSlug?: string;
    soldCount?: number;
  };
  className?: string;
  viewMode?: 'grid' | 'list';
}

// Simple localStorage-based wishlist for products
const WISHLIST_KEY = 'boli_product_wishlist';

function getWishlist(): string[] {
  try {
    const stored = localStorage.getItem(WISHLIST_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveWishlist(wishlist: string[]) {
  localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist));
}

export function ProductCard({ product, className, viewMode = 'grid' }: ProductCardProps) {
  const [isWished, setIsWished] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const { addItem, openDrawer } = useCartStore();

  useEffect(() => {
    setIsWished(getWishlist().includes(product.id));
  }, [product.id]);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isAddingToCart || product.stock === 0) return;

    setIsAddingToCart(true);
    const success = await addItem(product.id, 1);
    setIsAddingToCart(false);

    if (success) {
      setJustAdded(true);
      setTimeout(() => {
        setJustAdded(false);
        openDrawer();
      }, 500);
    }
  };

  const toggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const wishlist = getWishlist();
    let newWishlist: string[];

    if (isWished) {
      newWishlist = wishlist.filter((id) => id !== product.id);
    } else {
      newWishlist = [...wishlist, product.id];
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 300);
    }

    saveWishlist(newWishlist);
    setIsWished(!isWished);
  };

  const hasDiscount = product.compareAtPrice && product.compareAtPrice > product.price;
  const discountPercentage = hasDiscount
    ? Math.round(((product.compareAtPrice! - product.price) / product.compareAtPrice!) * 100)
    : 0;

  const isOutOfStock = product.stock === 0;
  const isLowStock = product.stock > 0 && product.stock <= 3;

  const getStockStatus = () => {
    if (isOutOfStock) {
      return { text: 'Out of Stock', className: 'bg-red-100 text-red-700' };
    }
    if (isLowStock) {
      return { text: `Only ${product.stock} left!`, className: 'bg-amber-100 text-amber-700' };
    }
    return { text: 'In Stock', className: 'bg-green-100 text-green-700' };
  };

  const stockStatus = getStockStatus();

  // List view layout (eBay-style horizontal card)
  if (viewMode === 'list') {
    return (
      <Link to={`/product/${product.id}`} className="block">
        <div
          className={cn(
            'group relative flex rounded-lg overflow-hidden bg-white border border-gray-200 shadow-sm transition-shadow duration-200 hover:shadow-md',
            className
          )}
        >
          {/* Image */}
          <div className="relative w-48 md:w-64 flex-shrink-0 overflow-hidden bg-gray-100">
            <img
              src={product.images?.[0] || '/placeholder-product.jpg'}
              alt={product.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />

            {/* Wishlist Button */}
            <button
              onClick={toggleWishlist}
              className={cn(
                'absolute top-2 right-2 z-10 p-2 bg-white/90 rounded-full shadow-sm transition-all duration-200',
                'hover:bg-white active:scale-95',
                isWished ? 'text-red-500' : 'text-gray-400 hover:text-gray-600'
              )}
              aria-label={isWished ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <Heart
                className={cn(
                  'w-5 h-5 transition-transform duration-200',
                  isWished && 'fill-current',
                  isAnimating && 'scale-125'
                )}
              />
            </button>

            {/* Discount badge */}
            {hasDiscount && (
              <div className="absolute top-2 left-2 z-10">
                <div className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                  {discountPercentage}% OFF
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 p-4 flex flex-col">
            {/* Title */}
            <h3 className="font-medium text-gray-900 text-base md:text-lg line-clamp-2 group-hover:text-primary group-hover:underline">
              {product.title}
            </h3>

            {/* Condition & Category */}
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              {product.condition && (
                <span className="capitalize">{product.condition}</span>
              )}
              {product.condition && product.category && <span>·</span>}
              {product.category && (
                <span className="capitalize">{product.category}</span>
              )}
            </div>

            {/* Seller Info & Rating */}
            {product.sellerName && (
              <div className="flex items-center gap-2 mt-2 text-sm">
                <span className="text-gray-600">{product.sellerName}</span>
                {product.sellerRating !== undefined && (
                  <span className="flex items-center gap-1 text-amber-500">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="text-gray-700">{product.sellerRating.toFixed(1)}</span>
                  </span>
                )}
              </div>
            )}

            {/* Price Section */}
            <div className="mt-auto pt-3">
              <div className="flex items-baseline gap-2">
                <span className="text-xl md:text-2xl font-bold text-gray-900">
                  {formatPKR(product.price)}
                </span>
                {hasDiscount && (
                  <span className="text-sm text-gray-500 line-through">
                    {formatPKR(product.compareAtPrice!)}
                  </span>
                )}
              </div>

              {/* Shipping & Stock Info */}
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                <span className="text-green-600">Free shipping</span>
                {product.soldCount && product.soldCount > 0 && (
                  <span className="text-red-600 font-medium">{product.soldCount} sold</span>
                )}
                {isLowStock && (
                  <span className="text-amber-600 font-medium">Only {product.stock} left!</span>
                )}
              </div>
            </div>

            {/* Add to Cart Button - List view */}
            <div className="mt-3">
              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock || isAddingToCart}
                className={cn(
                  'px-6 py-2 rounded-full text-sm font-semibold flex items-center justify-center gap-2 transition-all',
                  isOutOfStock
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : justAdded
                    ? 'bg-green-600 text-white'
                    : 'bg-primary text-white hover:bg-primary/90'
                )}
              >
                {isOutOfStock ? (
                  'Out of Stock'
                ) : isAddingToCart ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : justAdded ? (
                  <>
                    <Check className="w-4 h-4" />
                    Added!
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4" />
                    Add to Cart
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Grid view layout (default)
  return (
    <Link to={`/product/${product.id}`} className="block h-full">
      <div
        className={cn(
          'group relative flex flex-col h-full rounded-lg overflow-hidden bg-white border border-gray-200 shadow-sm transition-shadow duration-200 hover:shadow-md',
          className
        )}
      >
        {/* Image/Thumbnail */}
        <div className="relative aspect-square overflow-hidden bg-gray-100">
          <img
            src={product.images?.[0] || '/placeholder-product.jpg'}
            alt={product.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />

          {/* Wishlist Button (top right) */}
          <button
            onClick={toggleWishlist}
            className={cn(
              'absolute top-2 right-2 z-10 p-1.5 bg-white/90 rounded-full shadow-sm transition-all duration-200',
              'hover:bg-white active:scale-95',
              isWished ? 'text-red-500' : 'text-gray-400 hover:text-gray-600'
            )}
            aria-label={isWished ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart
              className={cn(
                'w-4 h-4 transition-transform duration-200',
                isWished && 'fill-current',
                isAnimating && 'scale-125'
              )}
            />
          </button>

          {/* Discount badge (top left) */}
          {hasDiscount && (
            <div className="absolute top-2 left-2 z-10">
              <div className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm uppercase tracking-wider">
                {discountPercentage}% OFF
              </div>
            </div>
          )}

          {/* Stock status (bottom left) */}
          <div className="absolute bottom-2 left-2 z-10">
            <span
              className={cn(
                'text-[10px] font-medium px-1.5 py-0.5 rounded',
                stockStatus.className
              )}
            >
              {stockStatus.text}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 flex flex-col flex-1 gap-1">
          {/* Title */}
          <h3 className="font-medium text-gray-900 text-sm line-clamp-2 leading-snug group-hover:text-primary group-hover:underline">
            {product.title}
          </h3>

          {/* Seller Info */}
          {product.sellerName && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <span className="truncate">{product.sellerName}</span>
              {product.sellerRating !== undefined && (
                <span className="flex items-center gap-0.5 text-amber-500">
                  <Star className="w-3 h-3 fill-current" />
                  {product.sellerRating.toFixed(1)}
                </span>
              )}
            </div>
          )}

          {/* Price */}
          <div className="mt-1">
            <span className="text-lg font-bold text-gray-900 block">
              {formatPKR(product.price)}
            </span>
            {hasDiscount && (
              <span className="text-xs text-gray-500 line-through">
                {formatPKR(product.compareAtPrice!)}
              </span>
            )}
          </div>

          {/* Sold count / Free shipping */}
          <div className="text-xs text-gray-500 mt-1">
            {product.soldCount && product.soldCount > 0 ? (
              <span className="text-red-600 font-medium">{product.soldCount} sold</span>
            ) : (
              <span className="text-green-600">Free shipping</span>
            )}
          </div>

          {/* Add to Cart / View button */}
          <div className="mt-auto pt-2">
            <button
              onClick={handleAddToCart}
              disabled={isOutOfStock || isAddingToCart}
              className={cn(
                'w-full py-2 px-3 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-all',
                isOutOfStock
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : justAdded
                  ? 'bg-green-600 text-white'
                  : 'bg-primary text-white hover:bg-primary/90'
              )}
            >
              {isOutOfStock ? (
                'Out of Stock'
              ) : isAddingToCart ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Adding...
                </>
              ) : justAdded ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  Added!
                </>
              ) : (
                <>
                  <ShoppingCart className="w-3.5 h-3.5" />
                  Add to Cart
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}

// eBay-style skeleton for ProductCard
export function ProductCardSkeleton({ viewMode = 'grid' }: { viewMode?: 'grid' | 'list' }) {
  if (viewMode === 'list') {
    return (
      <div className="flex rounded-lg overflow-hidden bg-white border border-gray-200 shadow-sm">
        <div className="w-48 md:w-64 flex-shrink-0 bg-gray-200 animate-pulse" />
        <div className="flex-1 p-4 space-y-3">
          <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse" />
          <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
          <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse" />
          <div className="pt-3 space-y-2">
            <div className="h-7 bg-gray-200 rounded w-1/4 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse" />
          </div>
          <div className="h-10 bg-gray-200 rounded-full w-32 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden bg-white border border-gray-200 shadow-sm h-full flex flex-col">
      <div className="aspect-square bg-gray-200 animate-pulse" />
      <div className="p-3 space-y-2 flex-1">
        <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
        <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse" />
        <div className="mt-2 pt-2 space-y-1">
          <div className="h-6 bg-gray-200 rounded w-1/3 animate-pulse" />
          <div className="h-3 bg-gray-200 rounded w-1/4 animate-pulse" />
        </div>
        <div className="mt-auto pt-2">
          <div className="h-8 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}
